/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';

import useGetAccessTokenSafely from '../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../gql';
import findExperienceQuery from '../../gql/queries/findExperienceQuery';
import useBrowserUUID from '../useBrowserUUID';
import { getIsBot, getValidatedZipcode } from '../../../state/ducks/App/App-Selectors';
import { getBrandName } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getUserType } from '../../../state/ducks/App/ducks/Common/Common-Selectors';
import { getIsAuthenticatedStatus, getUserEmailId } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPassportData, getPassportSubscriptionStatus } from '../../../state/ducks/Passport/Passport-Selectors';
import validatePassport from '../validatePassport';

const useExperience = ({
    interactionName,
    targeting = {},
    userAttributes: customUserAttributes = [],
    featureFlag = true, // default but negated to skip if statement
    isSessionHasAccount = false, // needed for checking if guest user has account
    type = 'content',
    apolloOptions = {},
    resolveIdentity = false,
    queryName = 'PersonalizedExperience',
    filterByZipCode = false,
    userInfo = {},
}) => {
    const isBot = useSelector(getIsBot);

    if (isBot || !featureFlag) {
        return {
            loading: false,
            error: false,
            data: null,
            variables: { skip: false },
        };
    }

    const brand = useSelector(getBrandName) || '1800flowers';
    const browserUUID = useBrowserUUID();
    const jwt = useGetAccessTokenSafely();

    // skip until jwt is loading
    const skip = Boolean(!jwt || !browserUUID || apolloOptions?.skip);

    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const passportData = useSelector(getPassportData);
    const isPassportMember = validatePassport('isActivePassportUser', passportData);
    const isPassportAdded = useSelector(getPassportSubscriptionStatus);
    const userType = useSelector(getUserType);
    const validatedZipCode = useSelector(getValidatedZipcode);
    const emailId = useSelector(getUserEmailId);

    let sessionCustomerType;
    if ((isPassportMember && isAuthenticated) || isPassportAdded) {
        sessionCustomerType = 'P'; // Passport members > Registered users
    } else if (isAuthenticated) {
        sessionCustomerType = 'R'; // Registered user, but not a Passport member
    } else {
        sessionCustomerType = 'G'; // Guest
    }

    const baseUserAttributes = [
        {
            name: 'sessionLoginState',
            value: userType === 'R' || userType === 'P' ? 'true' : 'false',
        },
        {
            name: 'sessionCustomerType',
            value: sessionCustomerType,
        },
    ];

    if (isSessionHasAccount) baseUserAttributes.push({ name: 'hasAccountCookie', value: 'true' });

    // custom attributes override base if same name
    const mergedUserAttributesMap = new Map();
    [...baseUserAttributes, ...customUserAttributes].forEach((attr) => {
        mergedUserAttributesMap.set(attr.name, attr);
    });
    const userAttributes = Array.from(mergedUserAttributesMap.values());

    const query = findExperienceQuery(type, queryName);

    const user = {
        anonymousId: browserUUID,
        ...userInfo,
    };
    if (filterByZipCode && validatedZipCode) user['zipCode'] = validatedZipCode;
    if (emailId) user['emailId'] = emailId;

    return useQuery(query, {
        variables: skip ? { skip } : {
            brand,
            environment: GRAPHQL_ENV,
            resolveIdentity,
            user,
            userAttributes,
            targeting: {
                interactionName,
                ...targeting,
            },
        },
        context: { api: 'personalizedContent', jwt },
        fetchPolicy: 'no-cache',
        skip,
        ...apolloOptions,
    });
};

export default useExperience;
